"use client"

import get from "lodash/get";

// helpers
import { useDimension } from "@/hooks/useDimensions";
import collections from "@/api/query/collections";
import locations from "@/api/query/locations";
import purposes from "@/api/query/purposes";
import currencies from "@/api/query/currencies";
import countryStateCity from "@/api/query/countryStateCity";

// interfaces
import { ISingleCollectionResponse } from "@/types/api/SingleCollectionResponse";
import { IFloor, ISingleLocationResponse } from "@/types/api/SingleLocationResponse";


export interface UseLocationDataInterface {
  slug?: string;
  preview?: string;
  isPreview?: boolean;
  params?: Record<string, unknown>;
  isAuthorized?: boolean;
  intiData?: ISingleLocationResponse,
}

export const useLocationSinglePageData = (args: UseLocationDataInterface) => {
  const { slug, preview, isPreview, params, isAuthorized, intiData } = args;
  const [width] = useDimension();

  const isTablet = width && width < 745;
  const locationUtils = locations.useLocation({
    slug,
    preview,
    config: { initialData: intiData, placeholderData: intiData }
  });

  const locationData = ((locationUtils && locationUtils.data)) as ISingleLocationResponse;

  const collectionsListUtils = collections.useCollectionsList({ config: { enabled: !!isPreview } });
  const searchSpaceUtils = locations.useLocationSpaces({
    slug: locationData?.slug,
    params: {
      limit: 100,
      status__in: preview ? "0__1__2__3" : null,
      ordering: "position",
      purpose__in: params?.purpose__in,
      type__in: params?.type__in,
      rate_daily__range: params?.rate_daily__range,
      width__gte: params?.width__gte,
      length__gte: params?.length__gte,
      size__in: params?.size__in,
      ranges__in: params?.start_date && params?.end_date ? `${params?.start_date}__${params?.end_date}` : null
    },
    config: { enabled: !!locationData?.id && !isPreview }
  });
  const countryUtils = countryStateCity.useCountriesFilters(
    { only_published: true }, { enabled: !isAuthorized }
  );

  const shouldBlurLocation = locationData?.accessRestricted && !isAuthorized && !preview;
  const collectionToRender = collectionsListUtils.data
    ?.filter(item => (locationData?.collections as unknown as number[])?.includes(item.id));
  const locationTypeToRender = ((isPreview ? collectionToRender : locationData?.collections) || []).map((item: ISingleCollectionResponse) => item.name).join(", ");

  const collectionsQuery = locationData?.collections.map((collection) => collection.id).join("__");

  const spacesList = get(searchSpaceUtils, "data.results", []);
  const floorOptions = get(locationData, "floors", []).map((item: IFloor) => ({ value: get(item, "image.url"), label: item.name, id: item.id }));

  // effects
  return {
    countryUtils,
    locationData,
    locationUtils,
    floorOptions,
    searchSpaceUtils,
    spacesList,
    isTablet,
    shouldBlurLocation,
    locationTypeToRender,
    collectionsQuery,
  };
};

export const useLocationSpacesContainerFiltersData = () => {
  const purposesListUtils = purposes.usePurposesList({ params: { is_search: true } });
  const currenciesListUtils = currencies.useCurrenciesList();

  const currencyOptions = (get(currenciesListUtils, "data", [])).map(item => ({ value: item.code, label: item.code, code: item.code }));
  const purposesOptions = (get(purposesListUtils, "data", [])).map(item => ({ value: item.id, label: item.name }));

  return {
    currencyOptions,
    purposesOptions
  };
};
